import {
  StaticGridResponse,
  StaticChampionResponse,
  StaticGridData,
  ChampionConfig
} from '../types/staticTypes';
import { SimplifiedChampion } from '../../XXX/gameTypes';
import { WaveBasedChampionPair, WaveExportRequest } from '../components/GridEditor/WaveExport/types';
import { WaveAnalysisResponse } from '../components/WaveAnalysisGrid';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static`;


async function handleJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

class StaticDataService {
  


  /**
   * Fetch static grid data for combo mode
   */
  async fetchComboGrid(championName: string, opponentName?: string, batchSize: number = 6): Promise<StaticGridData> {
    try {
      let url = `${API_BASE_URL}/grid/combo/${championName}`;
      const params = new URLSearchParams();
      
      if (opponentName) {
        params.append('opponent', opponentName);
      }
      params.append('batchSize', batchSize.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: StaticGridResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch combo grid data');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching combo grid:', error);
      throw error;
    }
  }

  /**
   * Fetch the next batch of champions for an existing grid
   */
  async fetchNextBatch(
    championName: string, 
    startLevel: number, 
    opponentName?: string, 
    batchSize: number = 6
  ): Promise<StaticGridData> {
    try {
      const response = await fetch(`${API_BASE_URL}/grid/next-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName,
          opponent: opponentName,
          startLevel,
          batchSize
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: StaticGridResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch next batch');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching next batch:', error);
      throw error;
    }
  }

  /**
   * Fetch static champion data at specific level
   */
  async fetchChampionAtLevel(
    championName: string, 
    level: number, 
    team: 'blue' | 'red' = 'blue'
  ): Promise<SimplifiedChampion> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/champion/${championName}/level/${level}?team=${team}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: StaticChampionResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch champion data');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching champion data:', error);
      throw error;
    }
  }

  /**
   * Fetch champion with specific configuration
   */
  async fetchChampionWithConfig(
    championName: string,
    level: number,
    items: string[] = [],
    runes: any = null,
    skillOrder: string[] = []
  ): Promise<SimplifiedChampion> {
    try {
      const response = await fetch(`${API_BASE_URL}/champion/configure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName,
          level,
          items,
          runes,
          skillOrder
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: StaticChampionResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch configured champion');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching configured champion:', error);
      throw error;
    }
  }

  /**
   * Fetch available opponents for a champion
   */
  async fetchOpponentOptions(championName: string): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/opponents/${championName}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch opponent options');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching opponent options:', error);
      throw error;
    }
  }

  /**
   * Fetch combo data for a champion at specific level
   */
  async fetchCombosAtLevel(championName: string, level: number): Promise<any[]> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/combos/${championName}/level/${level}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch combos');
      }
      
      return result.data;
    } catch (error) {
      console.error('Error fetching combos:', error);
      throw error;
    }
  }

  /**
   * Export wave-based champion pairs to create a new grid
   */
  async exportWaveBasedGrid(
    championPairs: WaveBasedChampionPair[],
    exportConfig: { mode: 'replace' | 'append'; customLabel?: string }
  ): Promise<StaticGridData> {
    try {
      const requestBody: WaveExportRequest = {
        championPairs,
        exportConfig
      };

      console.log('[staticDataService] Exporting wave-based grid with', championPairs.length, 'pairs');
      console.log('[staticDataService] Request body:', requestBody);
      console.log('[staticDataService] API URL:', `${API_BASE_URL}/grid/wave-export`);
      
      const response = await fetch(`${API_BASE_URL}/grid/wave-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('[staticDataService] Response status:', response.status);
      console.log('[staticDataService] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const result: StaticGridResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to export wave-based grid');
      }
      
      console.log('[staticDataService] ✅ Wave export successful, created', result.data.champions.length, 'rows');
      
      return result.data;
    } catch (error) {
      console.error('Error exporting wave-based grid:', error);
      throw error;
    }
  }

  /**
   * Fetch the current grid state (whatever is currently loaded on the backend)
   */
  async fetchCurrentGrid(): Promise<StaticGridData> {
    try {
      console.log('[staticDataService] Fetching current grid state');
      
      const response = await fetch(`${API_BASE_URL}/grid/current`);
      
      console.log('[staticDataService] Current grid response status:', response.status);
      console.log('[staticDataService] Current grid response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const result: StaticGridResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch current grid');
      }
      
      console.log('[staticDataService] ✅ Current grid fetched successfully, rows:', result.data.champions.length);
      
      return result.data;
    } catch (error) {
      console.error('[staticDataService] Error fetching current grid:', error);
      throw error;
    }
  }




  async exportWaveGrid(payload: WaveExportRequest): Promise<WaveAnalysisResponse> {
    const res = await fetch(`${API_BASE_URL}/grid/wave-export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<WaveAnalysisResponse>(res);
  }


  /**
   * Health check for the static API
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      return response.ok;
    } catch (error) {
      console.error('Static API health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const staticDataService = new StaticDataService();
export default staticDataService; 